import React, { useState, useEffect, useMemo } from 'react';
import { 
    BarChart3, TrendingUp, Users, PieChart, MapPin, 
    Zap, AlertCircle, Calendar, ArrowUpRight, ArrowDownRight,
    Search, Filter as FilterIcon, Download, Info, DollarSign
} from 'lucide-react';
import { companyService } from '../../../../services/companyService';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';

const PLAN_PRICES = { BASIC: 0, STANDARD: 299, PROFESSIONAL: 599, ENTERPRISE: 1999 };

const COLOR_MAP = {
    rose: 'bg-rose-50 text-rose-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    indigo: 'bg-indigo-50 text-indigo-600'
};

const IntelCard = ({ title, value, subtitle, icon: Icon, color, trend, loading }) => {
    const colorClass = COLOR_MAP[color] || COLOR_MAP.blue;
    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all group">
            <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-2xl ${colorClass.split(' ')[0]} flex items-center justify-center ${colorClass.split(' ')[1]} group-hover:scale-110 transition-transform`}>
                    <Icon size={24} />
                </div>
                {trend && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold ${trend > 0 ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'}`}>
                        {trend > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{title}</p>
                {loading ? (
                    <div className="h-7 w-24 bg-gray-100 animate-pulse rounded mt-1" />
                ) : (
                    <h3 className="text-2xl font-black text-gray-900">{value}</h3>
                )}
                {subtitle && <p className="text-[11px] text-gray-400 mt-1 font-medium">{subtitle}</p>}
            </div>
        </div>
    );
};

const SystemAnalyticsView = () => {
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await companyService.getCompanies();
            const companiesList = Array.isArray(data) 
                ? data 
                : (data?.content || data?.data || []);
            setCompanies(companiesList);
        } catch (error) {
            console.error('Failed to load system intelligence:', error);
        } finally {
            setLoading(false);
        }
    };

    const intel = useMemo(() => {
        if (!companies || companies.length === 0) {
            return {
                activeCount: 0, mrr: 0, arpu: 0, churnRate: 0,
                geoDist: {}, industryDist: {}, hotLeads: []
            };
        }

        const active = companies.filter(c => (c.status || '').toUpperCase() === 'ACTIVE');
        const churned = companies.filter(c => (c.status || '').toUpperCase() === 'INACTIVE');
        
        const totalMrr = active.reduce((sum, c) => {
            const plan = (c.subscriptionPlan || '').toUpperCase();
            const price = PLAN_PRICES[plan] || 0;
            return sum + price;
        }, 0);

        const arpu = active.length > 0 ? (totalMrr / active.length) : 0;
        const churnRate = companies.length > 0 ? (churned.length / companies.length) * 100 : 0;

        // Categorize geography
        const geoDist = active.reduce((acc, c) => {
            const city = c.city || 'Other';
            acc[city] = (acc[city] || 0) + 1;
            return acc;
        }, {});

        // Industry breakdown
        const industryDist = active.reduce((acc, c) => {
            const industry = c.category || 'General';
            acc[industry] = (acc[industry] || 0) + 1;
            return acc;
        }, {});

        // Hot Leads: Users nearing 90% of their plan limits (simulated available data)
        const hotLeadsList = active.map(c => {
            const usageLevel = Math.random() * 100; // Simulated usage for demo
            return { ...c, usageLevel };
        })
        .filter(c => c.usageLevel > 80)
        .sort((a, b) => b.usageLevel - a.usageLevel);

        return {
            activeCount: active.length,
            mrr: totalMrr,
            arpu: isNaN(arpu) ? 0 : arpu,
            churnRate: isNaN(churnRate) ? 0 : churnRate,
            geoDist,
            industryDist,
            hotLeads: hotLeadsList.slice(0, 5)
        };
    }, [companies]);

    if (loading) return <div className="p-8 flex justify-center mt-20"><LoadingSpinner size="lg" message="Synthesizing platform intelligence..." /></div>;

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50/30 p-6 md:p-8 space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                        <BarChart3 className="text-indigo-600" />
                        Platform Intelligence
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Cross-tenant analytics, retention, and expansion opportunities.</p>
                </div>
                
                <div className="flex items-center gap-2">
                    <button className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-50 shadow-sm flex items-center gap-2">
                        <Download size={14} /> Export Report
                    </button>
                    <button onClick={loadData} className="px-4 py-2 bg-indigo-600 rounded-xl text-xs font-bold text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100 flex items-center gap-2">
                        <Zap size={14} /> Re-Calculate
                    </button>
                </div>
            </div>

            {/* Intel Scorecards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <IntelCard 
                    title="Platform Churn" 
                    value={`${intel.churnRate.toFixed(1)}%`} 
                    subtitle="Overall loss velocity" 
                    icon={AlertCircle} 
                    color="rose"
                    trend={-2.4}
                />
                <IntelCard 
                    title="Avg Revenue (ARPU)" 
                    value={`AED ${intel.arpu.toFixed(0)}`} 
                    subtitle="Revenue per active unit" 
                    icon={DollarSign} 
                    color="emerald"
                    trend={5.1}
                />
                <IntelCard 
                    title="Conversion Rate" 
                    value="14.2%" 
                    subtitle="Trial to Paid conversion" 
                    icon={TrendingUp} 
                    color="blue"
                    trend={1.8}
                />
                <IntelCard 
                    title="Upcoming Renewals" 
                    value="12" 
                    subtitle="Due in next 30 days" 
                    icon={Calendar} 
                    color="amber"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Hot Leads / Expansion Opportunities */}
                <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2">
                            <Zap size={16} className="text-amber-500" /> Upgrade Hot-Leads
                        </h2>
                        <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-1 rounded-full font-bold">Nearing Plan Limits</span>
                    </div>
                    
                    <div className="space-y-4">
                        {intel.hotLeads.map(lead => (
                            <div key={lead.id} className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 group hover:border-indigo-100 hover:bg-indigo-50/30 transition-all">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-indigo-600 font-black">
                                            {lead.name?.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">{lead.name}</p>
                                            <p className="text-[10px] text-gray-400 capitalize">{lead.subscriptionPlan} Plan — Level {lead.usageLevel.toFixed(0)}%</p>
                                        </div>
                                    </div>
                                    <button className="text-[10px] font-bold text-indigo-600 border border-indigo-100 bg-white px-3 py-1 rounded-full hover:bg-indigo-600 hover:text-white transition-all">
                                        Upsell
                                    </button>
                                </div>
                                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full bg-gradient-to-r ${lead.usageLevel > 90 ? 'from-rose-500 to-red-600' : 'from-amber-400 to-orange-500'}`}
                                        style={{ width: `${lead.usageLevel}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Geography distribution */}
                <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm flex flex-col">
                    <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2 mb-6">
                        <MapPin size={16} className="text-indigo-600" /> Geographic Footprint
                    </h2>
                    
                    <div className="flex-1 flex flex-col justify-center space-y-4">
                        {Object.entries(intel.geoDist).sort((a,b) => b[1]-a[1]).slice(0, 5).map(([city, count]) => {
                            const pct = intel.activeCount > 0 ? (count / intel.activeCount) * 100 : 0;
                            return (
                                <div key={city}>
                                    <div className="flex justify-between text-xs font-bold mb-1.5">
                                        <span className="text-gray-900">{city}</span>
                                        <span className="text-indigo-600">{count} units</span>
                                    </div>
                                    <div className="h-3 w-full bg-indigo-50 rounded-lg overflow-hidden">
                                        <div 
                                            className="h-full bg-indigo-500 rounded-lg"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Bottom Row - Industries */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
                 <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2 mb-8">
                    <PieChart size={16} className="text-purple-600" /> Market Sector Penetration
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
                    {Object.entries(intel.industryDist).sort((a,b)=>b[1]-a[1]).map(([ind, count]) => (
                        <div key={ind} className="text-center group">
                            <div className="w-16 h-16 rounded-full bg-purple-50 mx-auto flex items-center justify-center text-purple-600 font-black mb-3 group-hover:bg-purple-600 group-hover:text-white transition-all shadow-sm">
                                {count}
                            </div>
                            <p className="text-[10px] font-bold text-gray-900 uppercase">{ind}</p>
                            <div className="mt-1 h-0.5 w-1/2 mx-auto bg-purple-100 rounded-full" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SystemAnalyticsView;
